import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./orderboard.module.css";
import Column from "./Column";
import OrderDrawer from "./OrderDrawer";
import {
  getColumnsAsync,
  getOrdersCurrentAsync,
  orderClose,
  updateOrderColumn,
} from "../../api/orderBoard";
import { SignalRContext } from "../NavBar";

export default function OrderBoard({ pick, jwt, signalRVersion }) {
  const [columns, setColumns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isLoad, setIsLoad] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    setIsLoad(true);

    getColumnsAsync(pick.id, jwt)
      .then((response) => response.json())
      .then((data) => setColumns(data ?? []))
      .catch(console.error)
      .finally(() => setIsLoad(false));

  }, [pick.id, jwt]);

  const loadOrders = useCallback(async () => {
    const response = await getOrdersCurrentAsync(jwt);
    if (!response.ok) {
      throw new Error(`Не удалось загрузить заказы (${response.status})`);
    }

    const data = await response.json();
    setOrders(data ?? []);
  }, [jwt]);

  useEffect(() => {
    loadOrders().catch(console.error);
  }, [loadOrders, signalRVersion]);

  useEffect(() => {
    if (!selectedOrder) return;

    const freshOrder = orders.find((item) => item.id === selectedOrder.id);
    if (freshOrder) {
      setSelectedOrder(freshOrder);
    }
  }, [orders, selectedOrder]);

  useEffect(() => {
    if (!selectedOrder) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedOrder]);

  const orderCloseCallback = useCallback(
    (orderId) => {
      orderClose(orderId, jwt).then((response) => {
        if (response.ok) {
          setOrders((prev) => prev.filter((item) => item.id !== orderId));
          setSelectedOrder((prev) => (prev?.id === orderId ? null : prev));
        }
      });
    },
    [jwt]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e, targetColumnId) => {
      e.preventDefault();

      const raw = sessionStorage.getItem("moveOrderBoard");
      if (!raw) return;

      try {
        const { columnId, orderId } = JSON.parse(raw);

        if (+columnId === +targetColumnId) return;

        const targetColumn = columns.find((item) => item.id === +targetColumnId);
        if (!targetColumn) return;

        updateOrderColumn(
          +orderId,
          +targetColumnId,
          targetColumn.clientProgressItem,
          jwt
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Не удалось обновить заказ (${response.status})`);
            }

            setOrders((prev) =>
              prev.map((item) =>
                item.id === +orderId
                  ? { ...item, columnId: +targetColumnId }
                  : item
              )
            );
          })
          .catch(() =>
            alert("К сожалению, пользователю не обновили статус заказа")
          );
      } catch (error) {
        console.error("Ошибка обработки drop:", error);
      }
    },
    [columns, jwt]
  );

  const sortedColumns = useMemo(() => columns ?? [], [columns]);

  SignalRContext.useSignalREffect("MoveColumn", ({ orderId, newColumnId }) => {
    setOrders((prev) =>
      prev.map((item) =>
        item.id === +orderId ? { ...item, columnId: +newColumnId } : item
      )
    );
  }, []);

  SignalRContext.useSignalREffect("CreateOrder", (order) => {
    setOrders((prev) => {
      const exists = prev.some((item) => item.id === order.id);
      if (exists) return prev;
      return [...prev, order];
    });
  }, []);

  if (isLoad) {
    return (
      <div className={styles.page}>
        <div className={styles.board}>
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className={styles.skeletonColumn}>
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonCard} />
              <div className={styles.skeletonCard} />
              <div className={styles.skeletonCardSmall} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.page}>
        <div className={styles.board}>
          {sortedColumns.map((column) => (
            <Column
              key={column.id}
              jwt={jwt}
              column={column}
              orders={orders.filter((item) => item.columnId === column.id)}
              handleDrop={handleDrop}
              handleDragOver={handleDragOver}
              onOpenOrder={setSelectedOrder}
            />
          ))}
        </div>
      </div>

      <OrderDrawer
        order={selectedOrder}
        jwt={jwt}
        orderClose={orderCloseCallback}
        onClose={() => setSelectedOrder(null)}
      />
    </>
  );
}
